import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import type { FormInstance } from "antd";
import { Select } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import { useSportsbookRestrictionDropdown } from "../../../../queries/dynamicBonuses/useSportsbookRestrictionDropdown";
import {
  RESTRICTION_DISPLAY_NAMES_PARAM_KEY,
  rememberRestrictionDisplayName,
  usableRestrictionDisplayName,
} from "./restrictionDisplayNames";
import type { SportsbookSearchType } from "../constants";
import type { WizardFormValues } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import styles from "../stylesDynamicBonuses.module.css";

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  form: FormInstance<WizardFormValues>;
  stepFieldName: number;
  paramKey: string;
  label: string;
  placeholder?: string;
  searchType: SportsbookSearchType;
  /** Reactive CSV value from the parent's Form.useWatch — drives chip display. */
  currentCSV: string | undefined;
  /** First selected tournament ID — enables the match dropdown (required by API). */
  firstTournamentId?: string;
  /** Number of tournaments currently selected — used to show multi-tournament hint for matches. */
  tournamentCount?: number;
  /** Remove this restriction entirely (clear value + hide the field). */
  onRemove?: () => void;
  onRestrictionDisplayName?: (id: string, name: string) => void;
};

function parseCSV(value: unknown): string[] {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * BONENG-980 — Searchable dropdown with chips for sportsbook restriction IDs.
 * Fetches tournaments/matches/markets from the Sportsbook endpoint, displays
 * them as `[Name, ID]`, and stores selected values as a CSV string on the form.
 */
const SportsbookRestrictionSearchField: React.FC<Props> = ({
  form,
  stepFieldName,
  paramKey,
  label,
  placeholder,
  searchType,
  currentCSV,
  firstTournamentId,
  tournamentCount,
  onRemove,
  onRestrictionDisplayName,
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const nameCache = useRef<Map<string, string>>(new Map());

  const needsTournament = searchType === "match" && !firstTournamentId;

  const { data: options = [], isLoading, isError } = useSportsbookRestrictionDropdown(
    searchType,
    debouncedSearch,
    !needsTournament,
    firstTournamentId,
  );

  useLayoutEffect(() => {
    for (const opt of options) {
      nameCache.current.set(opt.id, opt.name);
      rememberRestrictionDisplayName(opt.id, opt.name);
    }
  }, [options]);

  const selectedIds = useMemo(() => parseCSV(currentCSV), [currentCSV]);

  const selectedChips = useMemo(() => {
    return selectedIds.map((id) => {
      const cached = nameCache.current.get(id);
      const found = options.find((opt) => opt.id === id);
      const name = found?.name ?? cached ?? id;
      return { id, name };
    });
  }, [selectedIds, options]);

  useLayoutEffect(() => {
    const resultParams = {
      ...form.getFieldValue(["steps", stepFieldName, "result_params"]),
    };
    const labels = {
      ...((resultParams[RESTRICTION_DISPLAY_NAMES_PARAM_KEY] as
        | Record<string, string>
        | undefined) ?? {}),
    };
    const siblingLabels = {
      ...((form.getFieldValue([
        "steps",
        stepFieldName,
        "restriction_display_names",
      ]) as Record<string, string> | undefined) ?? {}),
    };
    let changed = false;
    for (const chip of selectedChips) {
      const title = usableRestrictionDisplayName(chip.id, chip.name);
      if (!title) continue;
      rememberRestrictionDisplayName(chip.id, title);
      onRestrictionDisplayName?.(chip.id, title);
      if (labels[chip.id] !== title) {
        labels[chip.id] = title;
        changed = true;
      }
      if (siblingLabels[chip.id] !== title) {
        siblingLabels[chip.id] = title;
        changed = true;
      }
    }
    if (!changed) return;
    resultParams[RESTRICTION_DISPLAY_NAMES_PARAM_KEY] = labels;
    form.setFieldValue(["steps", stepFieldName, "result_params"], resultParams);
    form.setFieldValue(
      ["steps", stepFieldName, "restriction_display_names"],
      siblingLabels,
    );
  }, [form, onRestrictionDisplayName, selectedChips, stepFieldName]);

  const updateFormValue = useCallback(
    (ids: string[], named?: { id: string; name: string }) => {
      const resultParams = {
        ...form.getFieldValue(["steps", stepFieldName, "result_params"]),
      };
      if (ids.length === 0) {
        delete resultParams[paramKey];
      } else {
        resultParams[paramKey] = ids.join(",");
      }
      const labels = {
        ...((resultParams[RESTRICTION_DISPLAY_NAMES_PARAM_KEY] as
          | Record<string, string>
          | undefined) ?? {}),
      };
      if (named) {
        const title = usableRestrictionDisplayName(named.id, named.name);
        if (title) {
          labels[named.id] = title;
        } else {
          delete labels[named.id];
        }
      }
      resultParams[RESTRICTION_DISPLAY_NAMES_PARAM_KEY] = labels;
      form.setFieldValue(["steps", stepFieldName, "result_params"], resultParams);
      form.setFieldValue(
        ["steps", stepFieldName, "restriction_display_names"],
        labels,
      );
    },
    [form, stepFieldName, paramKey],
  );

  const handleSelect = useCallback(
    (value: string) => {
      if (!selectedIds.includes(value)) {
        const name =
          options.find((opt) => opt.id === value)?.name ??
          nameCache.current.get(value) ??
          value;
        rememberRestrictionDisplayName(value, name);
        onRestrictionDisplayName?.(value, name);
        updateFormValue([...selectedIds, value], { id: value, name });
      }
      setSearch("");
    },
    [onRestrictionDisplayName, options, selectedIds, updateFormValue],
  );

  const handleRemove = useCallback(
    (id: string) => {
      updateFormValue(
        selectedIds.filter((existing) => existing !== id),
        { id, name: "" },
      );
    },
    [selectedIds, updateFormValue],
  );

  const filteredOptions = useMemo(
    () => options.filter((opt) => !selectedIds.includes(opt.id)),
    [options, selectedIds],
  );

  const selectOptions = useMemo(
    () =>
      filteredOptions.map((opt) => ({
        label: `${opt.name} (${opt.id})`,
        value: opt.id,
      })),
    [filteredOptions],
  );

  return (
    <div
      className={styles.sportsbookRestrictionField}
      data-testid={`sportsbook-restriction-${searchType}`}
    >
      <div className={styles.sportsbookRestrictionHeader}>
        <label className={styles.sportsbookRestrictionLabel}>{label}</label>
        {onRemove && (
          <button
            type="button"
            className={styles.sportsbookRestrictionRemove}
            aria-label={formatString(AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_REMOVE_RESTRICTION, { label })}
            onClick={onRemove}
          >
            <CloseOutlined />
          </button>
        )}
      </div>

      <Select
        showSearch
        placeholder={
          needsTournament
            ? AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SELECT_TOURNAMENT_FIRST
            : placeholder ?? formatString(AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SEARCH_PLACEHOLDER, { label: label.toLowerCase() })
        }
        value={undefined}
        searchValue={search}
        onSearch={setSearch}
        onSelect={(value) => handleSelect(value as unknown as string)}
        options={selectOptions}
        filterOption={false}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : isError ? (
            AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SEARCH_ERROR
          ) : (
            AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_NO_RESULTS
          )
        }
        suffixIcon={<SearchOutlined />}
        className={styles.sportsbookRestrictionSelect}
        disabled={needsTournament}
      />

      {selectedChips.length > 0 && (
        <div
          className={styles.sportsbookRestrictionChips}
          role="list"
          aria-label={formatString(AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SELECTED_RESTRICTIONS, { label })}
        >
          {selectedChips.map((chip) => (
            <span
              key={chip.id}
              className={styles.sportsbookRestrictionChip}
              role="listitem"
            >
              <span className={styles.sportsbookRestrictionChipLabel}>
                {chip.name} ({chip.id})
              </span>
              <button
                type="button"
                className={styles.sportsbookRestrictionChipRemove}
                aria-label={formatString(AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_REMOVE_ITEM, { name: chip.name })}
                onClick={() => handleRemove(chip.id)}
              >
                <CloseOutlined />
              </button>
            </span>
          ))}
        </div>
      )}

      {!needsTournament && (
        <p className={styles.sportsbookRestrictionHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_MULTI_SELECT_HINT}
        </p>
      )}

      {searchType === "match" && !needsTournament && (
        <p className={styles.sportsbookRestrictionHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_MATCH_DATE_HINT}
        </p>
      )}

      {searchType === "match" && (tournamentCount ?? 0) > 1 && (
        <p className={styles.sportsbookRestrictionHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_MATCH_FIRST_TOURNAMENT_HINT}
        </p>
      )}
    </div>
  );
};

export default SportsbookRestrictionSearchField;
